import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Game, PlayerScore } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

const Scoreboard: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [scores, setScores] = useState<PlayerScore[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [gameId]);

  const fetchData = async () => {
    try {
      const [gameRes, scoresRes] = await Promise.all([
        fetch(`${API_URL}/api/games/${gameId}`),
        fetch(`${API_URL}/api/games/${gameId}/scores`)
      ]);
      const gameData = await gameRes.json();
      const scoresData = await scoresRes.json();
      setGame(gameData);
      setScores(scoresData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getMolName = () => {
    if (!game || !game.molId) return null;
    const mol = game.players.find(p => p.id === game.molId);
    return mol?.name;
  };

  if (!game) {
    return <div className="page loading">Laden...</div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Scorebord</h1>
        <p>{game.name}</p>
      </div>

      {game.molId && (
        <div className="card" style={{ textAlign: 'center', background: 'var(--mol-green)' }}>
          <h2 style={{ color: 'white' }}>De Mol is onthuld!</h2>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--mol-accent)', marginTop: '10px' }}>
            {getMolName()}
          </div>
        </div>
      )}

      <div className="card">
        <h2>Ranglijst</h2>
        
        {scores.length === 0 ? (
          <div className="empty-state">
            <h3>Nog geen stemmen</h3>
            <p>Wacht tot spelers hun stemmen hebben uitgebracht</p>
          </div>
        ) : (
          <table className="score-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Speler</th>
                <th>Punten</th>
                <th>Stemmen</th>
              </tr>
            </thead>
            <tbody>
              {scores.map((score, index) => (
                <tr key={score.username}>
                  <td className="score-rank">{index + 1}</td>
                  <td>{score.username}</td>
                  <td className="score-points">{score.totalPoints}</td>
                  <td>{score.votes.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Vote History */}
      <div className="card">
        <h2>Stem Geschiedenis</h2>
        {game.votes.length === 0 ? (
          <div className="empty-state">
            <p>Nog geen stemmen uitgebracht</p>
          </div>
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {[...game.votes].reverse().map((vote, index) => (
              <div 
                key={vote.id || index} 
                style={{ 
                  padding: '10px', 
                  background: 'var(--mol-bg)', 
                  borderRadius: '8px',
                  marginBottom: '8px'
                }}
              >
                <div style={{ fontWeight: 'bold' }}>{vote.oddsUsername}</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--mol-text-muted)' }}>
                  Afl. {vote.episodeNumber}: {vote.points} punt{vote.points > 1 ? 'en' : ''} op {vote.oddsAgainstPlayerName}
                  {game.molId && (
                    <span style={{ 
                      marginLeft: '10px',
                      color: vote.suspectedMolId === game.molId ? 'var(--mol-green)' : 'var(--mol-red)'
                    }}>
                      {vote.suspectedMolId === game.molId ? '✓ Correct' : '✗ Fout'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button 
        className="btn btn-secondary btn-block" 
        onClick={() => navigate(`/game/${gameId}`)}
        style={{ marginTop: '20px' }}
      >
        ← Terug naar spel
      </button>
    </div>
  );
};

export default Scoreboard;
